import { WarehouseOrderStatus, PackageType, OrderClassification, TrackingSourceEnum } from '@/apis/warehouse';
import { isInternationalOrder } from '@/apis/warehouse';
import type { User } from '@/apis/users';
import type { OrderItem } from '@/apis/orders';
import type { InboundTrackingEntry } from '@/apis/warehouse';

export { isInternationalOrder } from '@/apis/warehouse';

/**
 * "All parts are enough" — same rule `PackingListSection`'s "All Items
 * Found" pill uses. Shared so the Ready-for-Shipping gating can't drift from
 * what the packing list itself shows.
 */
export function hasAllItemsFound(items: OrderItem[] | undefined): boolean {
  return (items ?? []).every((item) => (item.missedItemsQty ?? 0) === 0);
}

/**
 * Only the order's currently-assigned Salesperson or Handler may act on it —
 * Admin always bypasses. If a role's slot is still unclaimed (null), anyone
 * with the matching flag may claim it, since there's no current owner to
 * protect yet. Mirrors `OrderService.assertOrderOwnership` on the backend so
 * the UI's notion of "can I act here" never drifts from what the API allows.
 */
export function canActOnOrder(
  user: User | null | undefined,
  assignedSalespersonId: string | null | undefined,
  assignedWarehouseHandlerId: string | null | undefined
): boolean {
  if (!user) return false;
  if (user.userRole === 'admin') return true;
  if (user.id === assignedSalespersonId || user.id === assignedWarehouseHandlerId) return true;
  return (
    (!assignedSalespersonId && user.isSalesperson) ||
    (!assignedWarehouseHandlerId && user.isWarehouseHandler)
  );
}

/**
 * OrderClassification is a real but nullable column — many existing orders predate this field and
 * have it unset. Fall back to deriving it from the (also real) shipping country so the card badge,
 * the drawer badge, and the Classification filter all agree instead of three different answers.
 */
export function resolveClassification(
  orderClassification: OrderClassification | 'domestic' | 'international' | null | undefined,
  country: string | null | undefined
): OrderClassification {
  if (orderClassification) return orderClassification as OrderClassification;
  return isInternationalOrder(country) ? OrderClassification.INTERNATIONAL : OrderClassification.DOMESTIC;
}

/** Kanban column order + styling — mirrors ORDER_STATUS_STYLE in order-shared.tsx */
export const WAREHOUSE_STATUS_STYLE: Record<
  WarehouseOrderStatus,
  { label: string; bar: string; badgeBg: string }
> = {
  [WarehouseOrderStatus.AWAITING]: { label: 'Awaiting', bar: 'bg-warning', badgeBg: 'bg-warning-light text-warning' },
  [WarehouseOrderStatus.ALMOST_OPEN]: { label: 'Almost Open', bar: 'bg-orange-400', badgeBg: 'bg-orange-100 text-orange-500' },
  [WarehouseOrderStatus.OPEN_ORDERS]: { label: 'Open Orders', bar: 'bg-primary-s500', badgeBg: 'bg-primary-s50 text-primary-s500' },
  [WarehouseOrderStatus.LABEL_AWAITING]: { label: 'Label Awaiting', bar: 'bg-orange-500', badgeBg: 'bg-orange-100 text-orange-500' },
  [WarehouseOrderStatus.SHIPMENT_WILL_CALL]: { label: 'Shipment / Will Call', bar: 'bg-success', badgeBg: 'bg-success-light text-success' },
  [WarehouseOrderStatus.ISSUE]: { label: 'Issue', bar: 'bg-danger', badgeBg: 'bg-danger-light text-danger' },
  [WarehouseOrderStatus.COMPLETED]: { label: 'Completed', bar: 'bg-purple-600', badgeBg: 'bg-purple-100 text-purple-600' },
};

/** Column order — the backend only returns the columns the caller's role can see; render whatever keys are present, in this order. */
export const KANBAN_COLUMNS: WarehouseOrderStatus[] = [
  WarehouseOrderStatus.AWAITING,
  WarehouseOrderStatus.ALMOST_OPEN,
  WarehouseOrderStatus.OPEN_ORDERS,
  WarehouseOrderStatus.LABEL_AWAITING,
  WarehouseOrderStatus.SHIPMENT_WILL_CALL,
  WarehouseOrderStatus.ISSUE,
  WarehouseOrderStatus.COMPLETED,
];

/** Inbound tracking `status` is a free-text string server-side (no enum) — style known values, fall back to neutral for anything else. */
export const TRACKING_STATUS_STYLE: Record<string, { label: string; bg: string }> = {
  arrived: { label: 'Arrived', bg: 'bg-success-light text-success' },
  pending: { label: 'Pending', bg: 'bg-warning-light text-warning' },
  missing: { label: 'Missing', bg: 'bg-danger-light text-danger' },
};

export const DEFAULT_TRACKING_STATUS_STYLE = { bg: 'bg-smoke text-lightGray' };

/**
 * OrderItem.vendorStatus is a free-text column server-side (no enum, no constrained values) —
 * PATCH /warehouse/orders/:id/items accepts any string up to 20 chars. The UI writes exactly these
 * 3 canonical values (matching the backend DTO's own example) so status stays consistent.
 */
export const VENDOR_STATUS_VALUES = ['Pending', 'In Stock', 'Out of Stock'] as const;

function normalizeVendorStatus(status: string) {
  return status.trim().toLowerCase().replace(/\s+/g, '_');
}

export const VENDOR_STATUS_STYLE: Record<string, { label: string; bg: string }> = {
  pending: { label: 'Pending', bg: 'bg-warning-light text-warning' },
  in_stock: { label: 'In Stock', bg: 'bg-success-light text-success' },
  out_of_stock: { label: 'Out of Stock', bg: 'bg-danger-light text-danger' },
};

export const DEFAULT_VENDOR_STATUS_STYLE = { bg: 'bg-smoke text-lightGray' };

export function getVendorStatusStyle(status: string | undefined | null) {
  const key = normalizeVendorStatus(status || 'pending');
  return VENDOR_STATUS_STYLE[key] ?? { ...DEFAULT_VENDOR_STATUS_STYLE, label: status || 'Pending' };
}

export const PACKAGE_TYPE_LABEL: Record<PackageType, string> = {
  [PackageType.BOX]: 'Box',
  [PackageType.PALLET]: 'Pallet',
};

export const PACKAGE_TYPE_STYLE: Record<PackageType, string> = {
  [PackageType.BOX]: 'bg-primary-s50 text-primary-s500',
  [PackageType.PALLET]: 'bg-primary-s50 text-primary-s500',
};

export const CLASSIFICATION_LABEL: Record<OrderClassification, string> = {
  [OrderClassification.DOMESTIC]: 'Domestic',
  [OrderClassification.INTERNATIONAL]: 'International',
};

export const CLASSIFICATION_STYLE: Record<OrderClassification, string> = {
  [OrderClassification.DOMESTIC]: 'bg-purple-100 text-purple-600',
  [OrderClassification.INTERNATIONAL]: 'bg-orange-100 text-orange-500',
};

/** Fixed carrier choices for the tracking modal + comparison table — "Other" pairs with a free-text name, stored in `courierOrSupplierName`. */
export const CARRIER_OPTIONS = ['FedEx', 'UPS', 'DHL', 'USPS', 'Other'] as const;

export type TrackingRowStatus = 'matched' | 'waiting' | 'unmatched';

export interface TrackingComparisonRow {
  id: string;
  status: TrackingRowStatus;
  sales: InboundTrackingEntry | null;
  warehouse: InboundTrackingEntry | null;
}

function normalizeTrackingNumber(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * Greedily pairs each sales entry to a warehouse entry with the same
 * normalized tracking number (one-to-one) — mirrors
 * WarehouseService.computeTrackingMatch on the backend so the comparison
 * table and the order's Kanban status never disagree on what's "matched".
 *
 * Rows are sorted with every non-matched row (Waiting: a sales number with
 * no scan yet; Unmatched: a scan that doesn't correspond to any known sales
 * number) first, matched pairs in a section below — per the requirement
 * that the scan log surfaces problems regardless of the order entries were
 * typed/scanned in.
 */
export function pairTrackingEntries(entries: InboundTrackingEntry[]): TrackingComparisonRow[] {
  const salesEntries = entries.filter((e) => e.source === TrackingSourceEnum.SALES);
  const warehouseEntries = entries.filter((e) => e.source === TrackingSourceEnum.WAREHOUSE);
  const unmatchedWarehouse = [...warehouseEntries];

  const matchedRows: TrackingComparisonRow[] = [];
  const waitingRows: TrackingComparisonRow[] = [];

  for (const sale of salesEntries) {
    const normalizedSale = normalizeTrackingNumber(sale.trackingNumber);
    const matchIndex = normalizedSale
      ? unmatchedWarehouse.findIndex((w) => normalizeTrackingNumber(w.trackingNumber) === normalizedSale)
      : -1;
    if (matchIndex !== -1) {
      const [warehouse] = unmatchedWarehouse.splice(matchIndex, 1);
      matchedRows.push({ id: `${sale.id}:${warehouse.id}`, status: 'matched', sales: sale, warehouse });
    } else {
      waitingRows.push({ id: `sales:${sale.id}`, status: 'waiting', sales: sale, warehouse: null });
    }
  }

  const unmatchedRows: TrackingComparisonRow[] = unmatchedWarehouse.map((warehouse) => ({
    id: `warehouse:${warehouse.id}`,
    status: 'unmatched',
    sales: null,
    warehouse,
  }));

  return [...unmatchedRows, ...waitingRows, ...matchedRows];
}

export const TRACKING_ROW_STATUS_STYLE: Record<TrackingRowStatus, { label: string; bg: string }> = {
  matched: { label: 'Matched', bg: 'bg-success-light text-success' },
  waiting: { label: 'Waiting', bg: 'bg-warning-light text-warning' },
  unmatched: { label: 'Unmatched', bg: 'bg-danger-light text-danger' },
};

/** Whether the current user may edit/delete a given tracking entry — a salesperson only their own 'sales' entries, a handler only 'warehouse' entries, admin either. Mirrors the ownership split enforced server-side in WarehouseService.resolveTrackingSource. */
export function canActOnTrackingEntry(
  user: User | null | undefined,
  entry: InboundTrackingEntry
): boolean {
  if (!user) return false;
  if (user.userRole === 'admin') return true;
  if (entry.source === TrackingSourceEnum.SALES) return !!user.isSalesperson;
  return !!user.isWarehouseHandler;
}
